'use client'

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useGetOverviewQuery } from '@/lib/store/api/dashboardApi'
import { useGetPrescriptionRenewalDestinationQuery } from '@/lib/store/api/prescriptionRenewalApi'
import { prescriptionRenewalDashboardHref } from '@/lib/prescriptionRenewalHrefFromDestination'
import { PRESCRIPTION_RENEWAL_HREF } from '@/types/enums/sku-constants'
import { useOptionalTenant } from '@/contexts/TenantContext'

export interface PrescriptionRenewalDestination {
    href: string
    /** True until destination (and overview, when destination has no next step) has settled for this mount. */
    isLoading: boolean
    /**
     * Resolves to the destination href: immediately when already known, or once the
     * in-flight fetches land when it is not. Callers use this instead of `href`
     * for navigation so a click during the loading window still opens the right page.
     */
    waitForHref: () => Promise<string>
}

/**
 * Destination for prescription renewal entry points (dashboard and storefront).
 *
 * Prefers the dedicated destination API's next step (including lapsed cycles
 * overview may hide), then a `NextStep::PrescriptionRenewal` on the dashboard
 * overview, then the public 200007 storefront. `isLoading` lets a caller show a
 * spinner instead of hiding the entry point; `waitForHref` waits for those
 * queries to settle rather than navigating to a placeholder storefront.
 * Pass `skip` so logged-out surfaces do not call authenticated APIs.
 */
export function usePrescriptionRenewalDestination(options?: {
    skip?: boolean
    /** Public storefront renewal href when destination and overview have no next step. */
    fallbackHref?: string
}): PrescriptionRenewalDestination {
    const skip = options?.skip ?? false
    const tenantRenewalHref = useOptionalTenant()?.constants?.sku?.prescriptionRenewalHref
    const fallbackHref = options?.fallbackHref ?? tenantRenewalHref ?? PRESCRIPTION_RENEWAL_HREF
    const {
        data: destination,
        isError: destinationError,
        isFetching: destinationFetching,
    } = useGetPrescriptionRenewalDestinationQuery(undefined, {
        refetchOnMountOrArgChange: true,
        skip,
    })
    const {
        data: overview,
        isError: overviewError,
        isFetching: overviewFetching,
    } = useGetOverviewQuery(undefined, { skip })

    const href = useMemo(
        () =>
            skip
                ? fallbackHref
                : prescriptionRenewalDashboardHref(destination, overview, fallbackHref),
        [skip, destination, overview, fallbackHref],
    )

    const destinationPending = !skip && destinationFetching && destination === undefined && !destinationError
    const overviewPending = !skip && overviewFetching && overview === undefined && !overviewError
    const destinationResolved =
        Boolean(destination?.next_step_id) || Boolean(destination?.has_gm_prescribed_medications)
    const isLoading = destinationPending || (!destinationResolved && overviewPending)

    const latestRef = useRef({ href, isLoading })
    latestRef.current = { href, isLoading }

    const waitersRef = useRef<Array<(href: string) => void>>([])

    useEffect(() => {
        if (isLoading || waitersRef.current.length === 0) return

        const waiters = waitersRef.current
        waitersRef.current = []
        waiters.forEach((resolve) => resolve(href))
    }, [href, isLoading])

    const waitForHref = useCallback(() => {
        if (!latestRef.current.isLoading) return Promise.resolve(latestRef.current.href)

        return new Promise<string>((resolve) => {
            waitersRef.current.push(resolve)
        })
    }, [])

    return { href, isLoading, waitForHref }
}
